<template>
  <div
    key="video-panel"
    class="bg-black flex justify-center items-center"
    :style="isBigScreen ? 'height: calc(100vh - 180px);':''"
  >
    <!-- resolveVideoSource returns null for a Bunny video that is still
         encoding. The learn-time counter only starts on the player's
         'playing' event, so no player means no time is credited either. -->
    <div v-if="!videoSource" role="status" aria-live="polite"
         class="video-unavailable text-white text-center p-6">
      {{ $t('general.video-not-ready') }}
    </div>
    <div v-else class="video-player">
      <video ref="videoPlayer" class="video-js" controls></video>
    </div>
  </div>
</template>

<script>
// No `import '@videojs/http-streaming'` here: video.js 8 bundles VHS inside
// video.js itself and registers it as an Html5 source handler on import.
// Importing the standalone package as well pulled in a second copy (364 KB
// unminified) and registered a second handler for the same source types.
import videojs from 'video.js';
import {resolveVideoSource} from "@/utils/video-source";
import {createLearnTimeMeter} from "@/utils/learn-time";
import {createPlaybackLock} from "@/utils/playback-lock";
import Swal from 'sweetalert2';

// Every player event after which the played position may have moved or the
// playing state changed. timeupdate alone fires ~4x a second while playing.
const METER_EVENTS = ['playing', 'pause', 'waiting', 'seeking', 'seeked', 'ended', 'ratechange', 'timeupdate'];

export default {
  name: "MainPanelVdo",
  props: [
    'currentVdoSetTopic',
    'studentEnrollDetail',
    'keepCounting',
    'countLearningPercent',
    'isBigScreen',
    'forceStopVideo',
  ],
  data() {
    return {
      player: null,
      isPlaying: false,
      meterInterval: null,
    }
  },
  computed: {
    videoSource() {
      return resolveVideoSource(this.currentVdoSetTopic);
    },
  },
  watch: {
    // Watching the resolved source rather than the topic: the empty state and
    // the player are behind a v-if on exactly this value, so this is the
    // moment the <video> element appears or disappears.
    videoSource(source) {
      if (!source) {
        if (this.player) {
          // dispose() emits no pause/ended and tears the listeners down, so stop the
          // meter here: its interval must not outlive the player.
          this.stopMeter();
          this.isPlaying = false;
          this.player.dispose();
          this.player = null;
        }
        return;
      }
      if (this.player) {
        this.player.src(source);
        this.meter.reset();
        // Kept from the changeVideoSrc this replaces: switching topics has to
        // resume where the student left off.
        this.playVideoFromLastPosition();
        return;
      }
      this.$nextTick(() => {
        this.setUpVideoJs();
        if (this.player) {
          this.playVideoFromLastPosition();
        }
      });
    },
    forceStopVideo(val){
      if(val && this.player){
        this.stopMeter();
        // Same reason as the videoSource null branch above.
        this.isPlaying = false;
        this.player.dispose();
        this.player = null;
      }
    }
  },
  methods: {
    setUpVideoJs() {
      // The videoSource watcher defers this to $nextTick, so two source
      // changes inside one flush can queue two calls before this.player is
      // assigned by the first.
      if (this.player) return;

      const _this = this;

      _this.player = videojs(this.$refs.videoPlayer, {
        sources: [this.videoSource],
        controlBar: {
          // Learn time never exceeds real time (see readPlayback), so playing
          // at 2x covered the whole video for half the credit -- "I watched
          // it all and it says 50%". Playback is locked at 1x.
          // No playbackRates option either, below: leaving the button on with
          // no rates to choose from would just render a dead menu.
          playbackRateMenuButton: false,
          pictureInPictureToggle: false,
        },
        autoplay: true,
        fluid: false,
        controls: true,
        hotkeys: true,
        responsive: true,
        aspectRatio: '16:9',
        preload: 'metadata',
        nativeControlsForTouch: true,
        nativeControls: true,

      }, function onPlayerReady() {
        _this.$refs.videoPlayer.controls = true;

        // wait for video metadata to load, then set time
        _this.player.on('loadedmetadata', function() {
          // _this.setStartPositionWhenSetUp();

          let lastPosition = _this.getLastPosition();
          //if last play is end then start from beginning
          if (lastPosition === parseInt(_this.player.duration())) {
            lastPosition = 0;
          }

          _this.player.currentTime(lastPosition);
        });

        let initdone = false;
        // iPhone/iPad need to play first, then set the time
        // events: https://www.w3.org/TR/html5/embedded-content-0.html#mediaevents
        _this.player.on('canplaythrough', function() {
          if (!initdone) {
            _this.setStartPositionWhenSetUp();
            initdone = true;
          }
        });

        _this.player.on('timeupdate', async function () {
          _this.saveLastPosition(_this.player.currentTime());
        });

        // Work when video is playing
        _this.player.on('playing', function () {
          _this.isPlaying = true;
        });

        //when video are pause trigger time update
        // not good event to save
        _this.player.on(['waiting', 'pause'], async function () {
          // console.log('video waiting,pause');
          _this.isPlaying = false;
          _this.saveLastPosition(_this.player.currentTime());
          _this.$emit('onVideoStopped');
        });

        //when video end save progress and go to next episode
        _this.player.on('ended', async function () {
          // console.log('video ended');
          _this.isPlaying = false;
          _this.$emit('onVideoStopped');
        });

      });

      // The options above only close the video.js route. nativeControls and
      // nativeControlsForTouch are both on, so iOS Safari draws its own
      // controls with its own speed menu that no video.js option removes, and
      // devtools can set the rate anywhere. This is the enforcement.
      //
      // Guarded on !== 1 because setting the rate fires ratechange again;
      // without the guard that second event would set it again, and so on.
      _this.player.on('ratechange', function() {
        if (_this.player.playbackRate() !== 1) {
          _this.player.playbackRate(1);
        }
      });

      // Newest playback wins (src/utils/playback-lock.js).
      _this.player.on('playing', function() {
        _this.playbackLock.claim();
      });
      _this.startMeter();
    },
    playVideoFromLastPosition() {
      //auto play video when change
      const _this = this;
      setTimeout(function () {
        if (_this.player) {
          _this.player.play();
        }
      }, 500);

      //set position from last watch
      this.setStartPosition();
    },
    setStartPositionWhenSetUp() {
      let lastPosition = this.getLastPosition();
      //if last play is end then start from beginning
      if (lastPosition === parseInt(this.player.duration())) {
        lastPosition = 0;
      }

      this.player.currentTime(lastPosition);

      this.player.play();
    },
    setStartPosition() {
      let lastPosition = this.getLastPosition();

      //if last play is end then start from beginning
      if (lastPosition === parseInt(this.player.duration()) ||
        lastPosition >= parseInt(this.player.duration()) - 5) {
        lastPosition = 0;
      }

      this.player.on('loadedmetadata', function () {
        this.currentTime(lastPosition);
      });

    },
    getLastPosition(){
      let lastPosition = 0;

      //get last position from local storage
      lastPosition = localStorage.getItem(this.studentEnrollDetail.id + "-" + this.currentVdoSetTopic.id);
      if (!lastPosition) {
        lastPosition = 0
      }

      return parseInt(lastPosition);
    },
    saveLastPosition(currentTime) {
      localStorage.setItem(this.studentEnrollDetail.id + "-" + this.currentVdoSetTopic.id, currentTime)
    },
    //-------------------------- FOR UPDATE LEARN TIME --------------------------
    // Learn time is what the video actually played (src/utils/learn-time.js).
    // The old setInterval(addCounter, 1000) counted ticks, and browsers
    // throttle timers in a hidden page while the video keeps playing.
    readPlayback() {
      const player = this.player;
      if (!player) {
        return {position: 0, countable: false};
      }
      return {
        position: player.currentTime(),
        countable: !player.paused() && !player.ended() && !player.seeking() && player.playbackRate() === 1,
      };
    },
    creditPlayback(event) {
      let seconds = this.meter.checkpoint();
      if (event && event.type === 'ended') {
        // Played to the end: the fraction still pending is credited too,
        // or a clip whose length is whole seconds ends one second short.
        seconds += this.meter.finish();
      }
      if (seconds > 0 && this.keepCounting) {
        this.countLearningPercent(seconds);
      }
    },
    startMeter() {
      METER_EVENTS.forEach((event) => this.player.on(event, this.creditPlayback));
      // A checkpoint even when no event fires. Throttled to once a minute in
      // a hidden tab, which no longer matters: the next checkpoint credits
      // the whole gap.
      this.meterInterval = setInterval(this.creditPlayback, 1000);
      this.meter.reset();
    },
    stopMeter() {
      if (this.meterInterval) {
        clearInterval(this.meterInterval);
        this.meterInterval = null;
      }
    },
    // Another tab of this browser started playing. One account plays one
    // video at a time and the newest wins, so this one stops. A tab that is
    // already paused has nothing to give up and stays quiet.
    onPlaybackTakenOver() {
      if (!this.player || this.player.paused()) return;
      this.player.pause();
      Swal.fire({
        title: this.$t('virtual-class.playback-taken-over-title'),
        text: this.$t('virtual-class.playback-taken-over-message'),
        icon: 'warning',
        confirmButtonText: this.$t('general.close'),
      });
    },
  },
  created() {
    // Not in data(): the meter holds plain closures, nothing to render.
    this.meter = createLearnTimeMeter({
      now: () => Date.now(),
      read: () => this.readPlayback(),
    });
    this.playbackLock = createPlaybackLock({
      onTakenOver: () => this.onPlaybackTakenOver(),
    });
  },
  mounted() {
    // Without the videoSource check this runs against a $refs.videoPlayer that
    // v-if never rendered.
    if (!this.player && this.videoSource) {
      this.setUpVideoJs();
    }
  },
  beforeDestroy() {
    this.$emit('onVideoStopped');
    this.stopMeter();
    this.playbackLock.destroy();
    if (this.player) {
      this.player.dispose()
    }
  }
}
</script>

<style lang="scss" type="text/css">
//@import '/video.js/dist/video-js.min.css';

.video-player {
  width: 100% !important;
  height: auto !important;
  max-height: calc(100vh - 180px) !important;
  background: #22292f;
  overflow: hidden;
  position: relative;

  & .video-js {
    width: 100% !important;
    height: auto !important;
    padding-top: 0 !important;

    & video {
      width: 100% !important;
      height: auto !important;
      max-height: 100vh;
      @media only screen and (min-height: 600px) {
        max-height: calc(100vh - 200px);
      }
    }

    & .vjs-control-bar {
      display: none;
      z-index: 999999;
      position: absolute;
    }

    & .vjs-loading-spinner {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      // background: #0000005c;
      text-align: center;
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;
      display: none;
    }

    & .vjs-big-play-button {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      margin: auto;
      width: 200px;
      height: 200px;
      background: #0000005c;
      text-align: center;
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;
      border-radius: 50%;
      border-width: 5px;
      border-color: #ffffff5e;
      display: none;
    }

    & .vjs-control-bar {
      display: none;
      position: absolute;
      bottom: 0;
      height: 40px;
      background: red;
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      display: none;

      & > .vjs-control {
        margin: 10px;
      }

    }

    & .vjs-poster {
      display: none;
    }

    & .vjs-text-track-display {
      display: none;
    }

    & .vjs-error-display {
      display: none;
    }

    & .vjs-modal-dialog {
      display: none;
    }
  }
}
</style>
